import { DropdownMenu } from "radix-ui";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { reportFeature } from "@/analytics/features";
import { registerContext } from "@/analytics/notify";
import { SUPPORTED_LANGUAGES, setStoredLanguage } from "@/i18n/language";
import { Icon } from "./Icon";

interface LanguageMenuProps {
  children: ReactElement;
}

export const LanguageMenu: FC<LanguageMenuProps> = ({ children }) => {
  const { t, i18n } = useTranslation("components");

  const changeLanguage = (code: string) => {
    void i18n.changeLanguage(code).then(() => setStoredLanguage(code));
    registerContext({ locale: code });
    reportFeature("languageSwitch", code);
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="bg-card rounded-inputs shadow-raised z-[1301] min-w-[200px] overflow-hidden py-component-xsmall"
        >
          <DropdownMenu.Label className="px-component-medium py-component-xsmall text-xs font-semibold text-muted-foreground">
            {t("userInfo.language")}
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={i18n.resolvedLanguage} onValueChange={changeLanguage}>
            {SUPPORTED_LANGUAGES.map((language) => (
              <DropdownMenu.RadioItem
                key={language.code}
                value={language.code}
                className="flex items-center justify-between gap-component-medium px-component-medium py-component-small text-sm text-foreground outline-none cursor-pointer data-[highlighted]:bg-muted"
              >
                <span>{language.label}</span>
                <DropdownMenu.ItemIndicator aria-hidden className="flex items-center">
                  <Icon name="check" size="smaller" color="var(--accent-foreground)" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
